import {
  NoticeEditorMetadataResponse,
  PrivacyCenterDto,
  UpdatePrivacyCenter,
} from "../../common/service/server/dto/PrivacyCenterDto";
import { PublicPrivacyCenterDto } from "../../common/service/server/dto/PublicOrganizationNoticeDto";
import { addProtocolIfNotExists } from "./text";

type PrivacyDtos = PublicPrivacyCenterDto | PrivacyCenterDto;

export function privacyCenterSettingsUpdate(
  privacyCenter: PrivacyCenterDto,
  changes: UpdatePrivacyCenter,
): UpdatePrivacyCenter {
  // The legacy update endpoint resets some omitted settings. Preserve those settings,
  // but never round-trip generated intros unless an intro was explicitly edited.
  const settings = Object.fromEntries(
    Object.entries(privacyCenter).filter(([key]) => !key.endsWith("IntroText")),
  );
  return { ...settings, ...changes };
}

export type PrivacyCenterEmbeddableAssetType =
  | "LIST_OF_DATA_COLLECTED"
  | "DATA_DISCLOSURE_TABLE"
  | "LIST_OF_DATA_SOURCES"
  | "LIST_OF_DATA_USES"
  | "DATA_RETENTION_POLICY"
  | "WEB_COOKIES_TABLE";

const urlWithProtocol = (url: string, protocol = "https") => addProtocolIfNotExists(url, protocol);

export const removeUrlProtocol = (url: string): string =>
  url.trim().replace(/^(?:https?:\/\/)+/i, "");

const privacyCenterUrl = (privacyCenter: PrivacyDtos, suffix: string) => {
  return urlWithProtocol(`${privacyCenter.url}${suffix}`);
};

const getRootUrl = (privacyCenter: PrivacyDtos) => privacyCenterUrl(privacyCenter, "");
const getPrivacyPolicyUrl = (privacyCenter: PrivacyDtos) =>
  privacyCenterUrl(privacyCenter, "/privacy-policy");
const getCAPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "california-privacy-notice");

const getPrivacyPolicySectionUrl = (privacyCenter: PrivacyDtos, sectionAnchor: string) =>
  privacyCenterUrl(privacyCenter, `/privacy-policy#${sectionAnchor}`);

const getGDPRNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "eea-uk-privacy-notice");

const getPIPEDANoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "ca-pipeda-privacy-notice");

const getVAPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "virginia-privacy-notice");

const getCOPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "colorado-privacy-notice");

const getCTPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "connecticut-privacy-notice");

const getUTPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "utah-privacy-notice");

const getTXPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "texas-privacy-notice");

const getORPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "oregon-privacy-notice");

const getFLPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "florida-privacy-notice");

const getMTPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "montana-privacy-notice");

const getIAPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "iowa-privacy-notice");

const getDEPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "delaware-privacy-notice");

const getNHPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "new-hampshire-privacy-notice");

const getNEPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "nebraska-privacy-notice");

const getNJPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "new-jersey-privacy-notice");

const getTNPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "tennessee-privacy-notice");

const getMNPrivacyNoticeUrl = (privacyCenter: PrivacyDtos) =>
  getPrivacyPolicySectionUrl(privacyCenter, "minnesota-privacy-notice");

const getJobApplicantsNoticeUrl = (privacyCenter: PrivacyDtos) =>
  privacyCenterUrl(privacyCenter, "/job-applicant");

const getEmployeeNoticeUrl = (privacyCenter: PrivacyDtos) =>
  privacyCenterUrl(privacyCenter, "/employee");

const getContractorNoticeUrl = (privacyCenter: PrivacyDtos) =>
  privacyCenterUrl(privacyCenter, "/contractor");

const getEeaUkEmployeeNoticeUrl = (privacyCenter: PrivacyDtos) =>
  privacyCenterUrl(privacyCenter, "/employee-gdpr");

const getColoradoEmployeeBiometricNoticeUrl = (privacyCenter: PrivacyDtos) =>
  privacyCenterUrl(privacyCenter, "/colorado-employee-biometric-notice");

const getLimitUrl = (privacyCenter: PrivacyDtos) => privacyCenterUrl(privacyCenter, "/limit");

const getOptOutUrl = (privacyCenter: PrivacyDtos, isSelling: boolean, isSharing: boolean) => {
  const suffix = optOutSuffix(isSelling, isSharing);
  if (!suffix) {
    return null;
  }
  return privacyCenterUrl(privacyCenter, suffix);
};

const optOutSuffix = (isSelling: boolean, isSharing: boolean) => {
  if (isSelling || isSharing) {
    return "/opt-out";
  }
  return undefined;
};

export const PrivacyCenterUrls = {
  getRootUrl,
  getPrivacyPolicyUrl,
  getCAPrivacyNoticeUrl,
  getPrivacyPolicySectionUrl,
  getOptOutUrl,
  getJobApplicantsNoticeUrl,
  getEmployeeNoticeUrl,
  getContractorNoticeUrl,
  getEeaUkEmployeeNoticeUrl,
  getColoradoEmployeeBiometricNoticeUrl,
  getLimitUrl,
  getVAPrivacyNoticeUrl,
  getCOPrivacyNoticeUrl,
  getCTPrivacyNoticeUrl,
  getUTPrivacyNoticeUrl,
  getTXPrivacyNoticeUrl,
  getORPrivacyNoticeUrl,
  getFLPrivacyNoticeUrl,
  getMTPrivacyNoticeUrl,
  getIAPrivacyNoticeUrl,
  getDEPrivacyNoticeUrl,
  getNHPrivacyNoticeUrl,
  getNEPrivacyNoticeUrl,
  getNJPrivacyNoticeUrl,
  getTNPrivacyNoticeUrl,
  getMNPrivacyNoticeUrl,
  getGDPRNoticeUrl,
  getPIPEDANoticeUrl,
};

export const getPrivacyCenterCnameValue = (privacyCenter?: PrivacyDtos): string => {
  const id = privacyCenter?.id?.toLowerCase();
  if (!id) {
    return "";
  }
  return `${id}.perm.truevaultprivacycenter.com`;
};

export const CoreSectionTemplates = [
  "Introduction",
  "Changes_To_Privacy_Policy",
  "Information_Collection",
  "Why_We_Process_Your_Information",
  "How_We_Disclose_Your_Personal_Information",
  "Shopify_SNI",
  "Cookies_Similar_Technologies",
  "California_Shine_Light_Disclosure",
  "Contact_Us",
] as const;
export const InternationalSectionTemplates = ["EEA_UK_GDPR", "CANADA_PIPEDA"] as const;
export type CoreSectionTemplateType = typeof CoreSectionTemplates[number];
export type SectionTemplateType =
  | typeof CoreSectionTemplates[number]
  | typeof InternationalSectionTemplates[number];

export type NewSectionType = "Blank" | SectionTemplateType;

export const PRIVACY_CENTER_EMAIL_TOKEN = "%{PRIVACY_CENTER_EMAIL}";
export const PRIVACY_CENTER_BRAND_TOKEN = "%{PRIVACY_CENTER_BRAND}";

export const SectionTemplateTypeToSectionHeader: Record<SectionTemplateType, string> = {
  Introduction: "Introduction",
  Changes_To_Privacy_Policy: "Changes to This Privacy Policy",
  Information_Collection: "Information Collection",
  Why_We_Process_Your_Information: "Why We Process Your Information",
  How_We_Disclose_Your_Personal_Information: "How We Disclose Your Personal Information",
  Shopify_SNI: "Shopify Network Intelligence",
  Cookies_Similar_Technologies: "Cookies & Similar Technologies",
  California_Shine_Light_Disclosure: "California Shine the Light Disclosure",
  EEA_UK_GDPR: "EEA/UK/CH (GDPR)",
  CANADA_PIPEDA: "Canada (PIPEDA)",
  Contact_Us: "Contact Us",
};

export function getSectionContentsFromTemplate(
  templateType: SectionTemplateType,
  noticeEditorMetadata: NoticeEditorMetadataResponse,
  orgAddress: string | undefined | null,
  optOutUrl: string | undefined | null,
): string {
  switch (templateType) {
    case "Introduction":
      return `
        <p>${PRIVACY_CENTER_BRAND_TOKEN} ("${PRIVACY_CENTER_BRAND_TOKEN}", "we", "us", or "our") respects your privacy and is committed to protecting it through our compliance with this Privacy Policy.</p>
        <p>This Privacy Policy describes how we collect, use and share information about you as well as your rights and choices about such collection, use and sharing. It applies when you access or use our websites and other products and services (collectively, the “Services”), contact our service team, engage with us on social media, or otherwise interact with us.</p>
        <p>If you do not agree with our policies and practices, your choice is not to use the Services. By accessing or using the Services, whether or not you purchase any of our Services, you agree to this Privacy Policy.</p>
      `;
    case "Changes_To_Privacy_Policy":
      return `
      <p>We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the Privacy Policy and, in some cases, we may provide you with additional notice (such as adding a statement to our website homepage or sending you a notification). We encourage you to review the Privacy Policy whenever you access the Services or otherwise interact with us, as your continued use of any Services constitutes acceptance to the updated terms.</p>
      `;
    case "Information_Collection":
      return `
      <h3><b>Information collected from you</b></h3>
      <p>We collect the personal information you provide to us when you purchase our products or visit our website. The categories of information we may collect include:</p>
      ${getEmbeddableAssetHtml("LIST_OF_DATA_COLLECTED")}
      <h3><b>Information collected from other sources</b></h3>
      <p>We may collect personal information about you from third-party sources The sources and categories of information we may collect include:</p>
      ${getEmbeddableAssetHtml("LIST_OF_DATA_SOURCES")}
      <h3><b>How long we keep your data</b></h3>
      <p>We do not retain data for any longer than is necessary for the purposes described in this Privacy Policy. We generally retain data according to the guidelines below.</p>
      ${getEmbeddableAssetHtml("DATA_RETENTION_POLICY")}
      `;
    case "Why_We_Process_Your_Information":
      return `
      <p>We use personal information for the following purposes:</p>
      ${getEmbeddableAssetHtml("LIST_OF_DATA_USES")}
      `;
    case "How_We_Disclose_Your_Personal_Information":
      return `
      <p>We may disclose the following personal information about you to our services providers and vendors when you purchase our products or visit our website:</p>
      ${getEmbeddableAssetHtml("DATA_DISCLOSURE_TABLE")}
      `;
    case "Shopify_SNI":
      return `<p>Our online store is hosted by Shopify, and Shopify collects and processes personal data about store customers for purposes including but not limited to: providing product and store customization, analytics, and advertising services. Information submitted by customers will be shared with Shopify as well as third parties that may be located in other countries. To learn more about Shopify’s data practices, please review their <a href="https://www.shopify.com/legal/privacy/consumers" target="_blank" rel="noopener noreferrer">privacy policy</a>.</p>
      <p>Depending on your jurisdiction, you may opt out or object to Shopify’s processing of your data for certain use cases on their <a href="https://privacy.shopify.com/en" target="_blank" rel="noopener noreferrer">Privacy Controls</a> page.</p>`;
    case "Cookies_Similar_Technologies":
      return `
      <h3><b>Cookies</b></h3>
      <p>This site uses first and third-party cookies and similar technologies to process personal information for the purposes described in this Policy. Cookies are small text files placed on your device to store data that can be recalled by a web server in the same domain that placed the cookie. The text in a cookie often consists of a string of numbers and letters that uniquely identifies your device, but it can contain other information as well. To learn more about cookies and how to control the use of cookies at a browser level, visit <a href="https://allaboutcookies.org">https://allaboutcookies.org/</a></p>
      <h3><b>Do Not Track</b></h3>
      <p>Some browsers have incorporated “Do Not Track” (DNT) features that can send a signal to the websites you visit indicating you do not wish to be tracked. Because there is not a common understanding of how to interpret the DNT signal, our websites do not currently respond to browser DNT signals. However, you may still exercise your privacy rights as described elsewhere in this Policy.</p>
      <h3><b>Third party analytics tools</b></h3>
      <p>This site uses Google Analytics to analyze your interactions and experiences with our Services. You can find out how Google Analytics uses data <a href="https://support.google.com/analytics/answer/6004245">here</a> and how to opt out of Google Analytics <a href="https://tools.google.com/dlpage/gaoptout">here</a>. For more information on how Google uses data when you use our websites, click <a href="https://policies.google.com/technologies/partner-sites">here</a>.</p>
      `;
    case "California_Shine_Light_Disclosure":
      return `
      <p>If you are a California resident and have an established business relationship with us, you can request a notice disclosing the categories of personal information we have shared with third parties, for the third parties' direct marketing purposes, during the preceding calendar year. To request a notice, please submit your request to ${PRIVACY_CENTER_EMAIL_TOKEN}. Please allow 30 days for a response.</p>
      <p>To opt out of selling and sharing personal information, go to <a href="${optOutUrl}">Your Privacy Choices</a>.</p>
      `;
    case "EEA_UK_GDPR":
      return noticeEditorMetadata?.gdprTemplate || "";
    case "CANADA_PIPEDA":
      return noticeEditorMetadata?.pipedaTemplate || "";
    case "Contact_Us":
      return `
      <p>If you have any questions about this Privacy Policy, please contact us as follows:</p>
      <br>
      <p>${PRIVACY_CENTER_BRAND_TOKEN}</p>
      ${orgAddress && orgAddress.length > 0 ? `<p>${orgAddress}</p>` : ""}
      <p>${PRIVACY_CENTER_EMAIL_TOKEN}</p>
      `;
    default:
      throw new Error(`Invalid template type: ${templateType}`);
  }
}

export function getEmbeddableAssetHtml(assetType: PrivacyCenterEmbeddableAssetType): string {
  const pngImagesForAssetType = {
    LIST_OF_DATA_COLLECTED: "embedded-list-of-data-collected",
    DATA_DISCLOSURE_TABLE: "embedded-data-disclosure-table",
    LIST_OF_DATA_SOURCES: "embedded-list-of-data-sources",
    LIST_OF_DATA_USES: "embedded-list-of-data-uses",
    DATA_RETENTION_POLICY: "embedded-data-retention-policy",
    WEB_COOKIES_TABLE: "embedded-web-cookies-table",
  };

  return `
    <figure inert>
      <img x-truevault-asset-type="${assetType}" src="/assets/images/stay_compliant/privacy_center/${pngImagesForAssetType[assetType]}.png" srcset="/assets/images/stay_compliant/privacy_center/${pngImagesForAssetType[assetType]}@2x.png 2x" />
    </figure>
  `;
}
